import api, { publicReportFetch } from '@/lib/api/client'

import { StudyFiguresMeta, StudyFiguresRequest, StudyFiguresView } from '@digital-twins/shared-types'

/**
 * Who the survey tab is drawn for: the study owner (authenticated, by study
 * id) or a share link (guest, by share token). The share route serves question
 * and assigned-asset cuts only (plots plan D2).
 */
export interface StudyFiguresAccess {
  studyId?: string
  shareToken?: string
}

/**
 * One batched figures request (plots plan D2/D3): every requested question for
 * the (filtered) sample and each group of one cut. Python owns every number;
 * this only transports the request.
 */
export async function fetchStudyFigures(
  access: StudyFiguresAccess,
  request: StudyFiguresRequest,
  signal?: AbortSignal,
): Promise<StudyFiguresView> {
  if (access.shareToken) {
    return publicReportFetch<StudyFiguresView>(access.shareToken, '/figures', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    })
  }
  if (!access.studyId) {
    throw new Error('fetchStudyFigures - a study id or a share token is required')
  }
  const response = await api.post<StudyFiguresView>(
    `/experiments/${access.studyId}/figures`,
    request,
    { signal },
  )
  return response.data
}

/**
 * The cuts the survey tab may offer (plots plan D3): only cuts the figures
 * route forms, each with its groups' sizes, complement groups named in the UI
 * `locale`. The share route lists question and assigned-asset cuts only.
 */
export async function fetchStudyFiguresMeta(
  access: StudyFiguresAccess,
  locale: string | undefined,
  signal?: AbortSignal,
): Promise<StudyFiguresMeta> {
  const params = locale ? { locale } : undefined
  if (access.shareToken) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : ''
    return publicReportFetch<StudyFiguresMeta>(access.shareToken, `/figures/meta${query}`, { signal })
  }
  if (!access.studyId) {
    throw new Error('fetchStudyFiguresMeta - a study id or a share token is required')
  }
  const response = await api.get<StudyFiguresMeta>(`/experiments/${access.studyId}/figures/meta`, {
    params,
    signal,
  })
  return response.data
}
